import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api, errMsg, fmtDateTime, money, ROLE_LABELS, statusClass, statusLabel, hearingType } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge } from "@/components/Shared";
import { ArbitratorPanel, ArbitratorRequests } from "@/components/ArbitratorPanel";
import { Button } from "@/components/ui/button";

const Stat = ({ label, value, sub, testId }) => (
  <div className="border border-border bg-card p-6" data-testid={testId}>
    <p className="label-caps">{label}</p>
    <p className="font-display text-3xl mt-3 tracking-tight">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [channels, setChannels] = useState(null);
  const [myCases, setMyCases] = useState([]);

  useEffect(() => {
    if (user?.role === "advocate") {
      api.get("/cases").then(({ data }) => setMyCases(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    api.get("/notifications/channels").then(({ data }) => setChannels(data)).catch(() => {});
  }, []);

  const load = () => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const runReminders = async () => {
    try {
      const { data } = await api.post("/notifications/run-reminders");
      toast.success(`${data.reminders_issued} sitting reminder(s) and ${data.payment_reminders_issued} payment nudge(s) issued`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!stats) {
    return (
      <div className="space-y-4" data-testid="dashboard-loading">
        {[1, 2, 3].map((i) => <div key={i} className="h-28 border border-border bg-muted animate-pulse" />)}
      </div>
    );
  }

  const isClient = user?.role === "client";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={`${ROLE_LABELS[user?.role]} dashboard`}
        title={`Good day, ${user?.name?.split(" ").slice(-1)[0] || ""}`}
        description={
          isClient
            ? "Your references, their present stage and the next date of sitting. Your advocate's contact is shown; the tribunal's contact is confidential."
            : "Your references, sittings, adjournment requests and fee position at a glance."
        }
        testId="dashboard-header"
        actions={
          !isClient && (
            <Button data-testid="run-reminders-button" onClick={runReminders}
              className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
              <BellRing className="h-4 w-4 mr-2" /> Issue reminders
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <Stat label="References" value={stats.total_cases} testId="stat-total-cases" />
        <Stat label="Active" value={stats.active_cases} sub="Not closed / awarded" testId="stat-active-cases" />
        {user?.role === "arbitrator" ? (
          <Stat label="Awaiting verification" value={stats.pending_verification} testId="stat-pending-verification" />
        ) : (
          <Stat label="Filings" value={stats.documents} testId="stat-documents" />
        )}
        {isClient ? (
          <Stat label="Unread alerts" value={stats.unread_alerts} testId="stat-alerts" />
        ) : (
          <Stat label="Fees outstanding" value={money(stats.outstanding_fees)}
            sub={`Collected: ${money(stats.collected_fees)}`} testId="stat-fees" />
        )}
      </div>

      {user?.role === "arbitrator" && user?.approval === "pending" && (
        <div className="mt-6 border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-5 text-sm" data-testid="approval-pending-banner">
          <p className="label-caps">Panel approval pending</p>
          <p className="mt-1">Your arbitrator profile is under review by the AdhocArb registry. Advocates can select you for new references once it is approved; matters on which you are already tagged remain accessible.</p>
        </div>
      )}
      {user?.role === "arbitrator" && <ArbitratorRequests onChange={load} />}
      {user?.role === "advocate" && <ArbitratorPanel cases={myCases} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 border border-border bg-card" data-testid="upcoming-proceedings-panel">
          <div className="px-6 py-4 border-b border-border"><p className="label-caps">Next sittings</p></div>
          {stats.upcoming_proceedings.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">
              No sittings posted. {user?.role === "arbitrator" ? "Post a date from the case file." : "Await the tribunal's posting."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {stats.upcoming_proceedings.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0" data-testid={`dash-proceeding-${p.id}`}>
                    <td className="p-4 align-top">
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        <Badge className={hearingType(p.hearing_type).cls}>{hearingType(p.hearing_type).label}</Badge>{p.purpose}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 font-mono">
                        <Link to={`/cases/${p.case_id}`} className="text-blue-900 dark:text-blue-400 hover:underline">
                          {p.case_number !== "—" ? p.case_number : p.ref_id}
                        </Link> · {p.case_title}
                      </p>
                    </td>
                    <td className="p-4 align-top whitespace-nowrap text-muted-foreground">{fmtDateTime(p.scheduled_at)}</td>
                    <td className="p-4 align-top text-xs uppercase tracking-wider text-muted-foreground">{p.mode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-border bg-card" data-testid="stage-panel">
          <div className="px-6 py-4 border-b border-border"><p className="label-caps">Stage of proceedings</p></div>
          <div className="p-6 space-y-3">
            {Object.keys(stats.cases_by_status).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No references yet. <Link to="/cases" className="text-blue-900 dark:text-blue-400 underline">Open my cases</Link>.
              </p>
            ) : (
              Object.entries(stats.cases_by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-border px-3 py-2">
                  <Badge className={statusClass(k)}>{statusLabel(k)}</Badge>
                  <span className="font-mono text-sm">{v}</span>
                </div>
              ))
            )}
            {!isClient && (
              <div className="pt-3 border-t border-border">
                <p className="label-caps">Adjournments pending</p>
                <p className="font-display text-2xl mt-2" data-testid="stat-pending-adjournments">{stats.pending_adjournments}</p>
              </div>
            )}
            {channels && (
              <div className="pt-3 border-t border-border" data-testid="channel-status">
                <p className="label-caps">Alert channels</p>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground font-mono">
                  <li>Email: {channels.email}</li>
                  <li>SMS: {channels.sms}</li>
                  <li>WhatsApp: {channels.whatsapp}</li>
                  <li>Auto sweep: {channels.daily_sweep_ist}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
